import React, { useEffect, useRef, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Container, CardBody } from "react-bootstrap";
import { Bounce, toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PageTitleConstants } from "../../Constants";
import "./ContractForm.css";
import ContractFormStatuses from "./ContractFormStatuses";
import UserContext from "../../Contexts/UserContext";
import { DocumentsService, SignService, ConfigService } from "../../Services";
import { M1_ContractStatuses } from "../../Constants/Enums";
import { SignApplicationModal } from "../../Components";
import { AppHelpers } from "../../Helpers/App/AppHelpers";
import {
  faEdit,
  faFile,
  faFilePdf,
  faStar,
} from "@fortawesome/free-regular-svg-icons";
import {
  PageHeader,
  CheckboxControl,
  Loading,
  downloadFileFromResponse,
  toastSuccessMessage,
} from "wup-module1-base";
import { IconInfo } from "../../images/icons";
import { DateHelpers } from "../../Helpers/Date/DateHelpers";

function ContractForm({ contractNumber = 1 }) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [isIframeScrolled, setIsIframeScrolled] = useState(false);
  const [isApplicationModalOpen, setIsApplicationModalOpen] = useState(false);
  const [isSignAvailable, setIsSignAvailable] = useState(false);
  const [contractData, setContractData] = useState();
  const [currentStep, setCurrentStep] = useState();
  const [taskList, setTaskList] = useState();
  const [description, setDescription] = useState();
  const [timeToConclusionLeft, setTimeToConclusionLeft] = useState();
  const [timeToConclusionComponent, setTimeToConclusionComponent] = useState(<></>);
  const [isDeadlineToSignEnabled, setIsDeadlineToSignEnabled] = useState(false);
  const [deadlineToSignDate, setDeadlineToSignDate] = useState(new Date());
  const [deadlineToSignPassed, setDeadlineToSignPassed] = useState(false);
  const [hideSignButton, setHideSignButton] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const openSignModal = () => {
    updatePassedDeadline((show = true) => {
      if (!show) {
        const message = `${DateHelpers.formatFullDateHours(deadlineToSignDate)} upłynął termin podpisania umowy.`;

        toast.warn(message, {
          position: "top-right",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });

        return;
      }

      setIsApplicationModalOpen(true);
    });
  };

  const goBack = () => {
    navigate(`/`);
  };

  const updatePassedDeadline = (callback = () => { }) => {
    ConfigService.getDeadlinesFormattedToContext(user).then((data) => {
      const recruitmentTypes = user?.contractRecruitmentTypes ?? {};
      const contractIdx = Number(contractNumber);
      const type = recruitmentTypes[contractIdx.toString()];

      const contractDeadlinesMap = {
        1: {
          special: {
            enabled: data.isFirstContractSpecialDeadlineEnabled,
            passed: data.isFirstContractSpecialPassTimeToSign,
            date: data.firstContractSpecialTimeToSign,
          },
          standard: {
            enabled: data.isFirstContractStandardDeadlineEnabled,
            passed: data.isFirstContractStandardPassTimeToSign,
            date: data.firstContractStandardTimeToSign,
          },
          new: {
            enabled: data.isFirstContractNewDeadlineEnabled,
            passed: data.isFirstContractNewPassTimeToSign,
            date: data.firstContractNewTimeToSign,
          },
        },
        2: {
          special: {
            enabled: data.isSecondContractSpecialDeadlineEnabled,
            passed: data.isSecondContractSpecialPassTimeToSign,
            date: data.secondContractSpecialTimeToSign,
          },
          standard: {
            enabled: data.isSecondContractStandardDeadlineEnabled,
            passed: data.isSecondContractStandardPassTimeToSign,
            date: data.secondContractStandardTimeToSign,
          },
          new: {
            enabled: data.isSecondContractNewDeadlineEnabled,
            passed: data.isSecondContractNewPassTimeToSign,
            date: data.secondContractNewTimeToSign,
          },
        },
      };

      const contractDeadlinesData = contractDeadlinesMap[contractIdx]?.[type];
      if (!contractDeadlinesData) {
        console.warn(`No deadline data found for contract ${contractIdx} with type ${type}`);
        callback(false);
        return;
      }

      const { enabled, passed, date } = contractDeadlinesData;

      setIsDeadlineToSignEnabled(enabled);
      setDeadlineToSignPassed(passed);
      setDeadlineToSignDate(date);

      const hide = enabled && passed;
      setHideSignButton(hide);

      if (!enabled && !contractData?.conclusion_deadline) {
        console.warn(`No deadline data found`);
        callback(false);
        return;
      }
      
      const deadline = enabled ? date : new Date(contractData.conclusion_deadline);
      const timeLeft = DateHelpers.getStringDiffFromNow(deadline);
      setTimeToConclusionLeft(timeLeft);

      let component;
      if (enabled) {
        component = timeLeft ? (
          <React.Fragment>
            Termin podpisania umowy to <strong>{DateHelpers.formatFullDateHours(deadline)}</strong>.
          </React.Fragment>
        ) : (
          <React.Fragment>
            Termin podpisania umowy upłynął <strong>{DateHelpers.formatFullDateHours(deadline)}</strong>.
          </React.Fragment>
        );
      } else {
        component = timeLeft ? (
          <React.Fragment>
            Pozostało <strong>{timeLeft}</strong> do złożenia podpisu.
          </React.Fragment>
        ) : (
          <React.Fragment>
            <strong>Czas na podpisanie umowy upłynął.</strong>
          </React.Fragment>
        );
      }

      setTimeToConclusionComponent(component)
      callback(!hide);
    });
  }

  const downloadContract = (typeOfFile) => {
    DocumentsService.downloadContractByUuid(
      contractData.document_uuid,
      typeOfFile
    )
      .then((res) => {
        downloadFileFromResponse(res);
        toastSuccessMessage("Pobieranie umowy");
      })
      .catch((error) => {
        console.error(error);
      });
  };

  // hook wczytujący umowę użytkownika, oraz jej status
  useEffect(() => {
    DocumentsService.getContractDataByClientId(user.id, contractNumber)
      .then((response) => {
        let includingStatusObj = Object.keys(M1_ContractStatuses).find(
          (status) =>
            M1_ContractStatuses[status].range.includes(response.data.status_id)
        );

        setCurrentStep(M1_ContractStatuses[includingStatusObj].value);
        setContractData(response.data);
        updatePassedDeadline();
      })
      .catch((error) => {
        setCurrentStep(M1_ContractStatuses.ContactInPreparation.value);
        setIsLoaded(true);
      });
  }, []);

  // hook obsługujący komunikaty podpisu oraz komunikaty informacyjne
  useEffect(() => {
    let urlParams = new URLSearchParams(window.location.search);
    let result = urlParams.get("result");

    if (!(currentStep === M1_ContractStatuses.ContractInProgress.value)) {
      if (result === "1") {
        toast.success(
          <div>
            <FontAwesomeIcon icon={faEdit} /> Podpisano pomyślnie!
          </div>,
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          }
        );
      }
    }

    if (currentStep === M1_ContractStatuses.ContractToSign.value) {
      if (result === "0") {
        toast.error(
          <div> Umowa nie została podpisana. Spróbuj ponownie!</div>,
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          }
        );
      }

      updatePassedDeadline();
      ConfigService.getConfigByKey(`instructions_for_signing_contracts`)
        .then((response) => {
          setDescription(response.data.value);
        })
        .catch((error) => {
          console.error(error);
        });
    }

    if (currentStep === M1_ContractStatuses.ContactInPreparation.value) {
      setDescription("Umowa jest w trakcie przygotowania");
    }

    if (currentStep === M1_ContractStatuses.ContractAgreement.value) {
      DocumentsService.getContractAccountNumberAndStatus(user.id, contractNumber)
        .then((response) => {
          let descriptionText = "";

          !!response.data.conclusionDate &&
            (descriptionText += `<span>
            <strong>Data zawarcia: </strong>
            ${response.data.conclusionDate}
            </span><br/>`);

          !!response.data.vouchersNumber &&
            (descriptionText += `<span>
            <strong>Liczba bonów: </strong>
            ${response.data.vouchersNumber}
            </span><br/>`);

          !!response.data.bankAccountNumber &&
            (descriptionText += `<span>
            <strong>Numer konta do wpłaty wkładu własnego: </strong>
            ${response.data.bankAccountNumber}
            </span><br/>`);

          !!response.data.paymentDeadline &&
            (descriptionText += `<span>
            <strong>Termin do którego należy dokonać wpłaty: </strong>
            ${response.data.paymentDeadline}
            </span>`);

          setDescription(descriptionText);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [currentStep, isDeadlineToSignEnabled]);

  // hook obsługujący przwijanie iframe na sam dół oraz wykonuje getTaskList dla podpisu kwalifikowanego
  useEffect(() => {
    if (
      !!contractData &&
      !isIframeScrolled &&
      !(currentStep === M1_ContractStatuses.ContactInPreparation.value)
    ) {
      const iframe = iframeRef.current;

      const handleScroll = () => {
        const iframeDocument =
          iframe.contentDocument || iframe.contentWindow.document;
        const iframeWindow = iframe.contentWindow;

        const scrollTop =
          iframeDocument.documentElement.scrollTop ||
          iframeDocument.body.scrollTop;
        const scrollHeight = iframeDocument.documentElement.scrollHeight;
        const clientHeight =
          iframeWindow.innerHeight ||
          iframeDocument.documentElement.clientHeight ||
          iframeDocument.body.clientHeight;

        if (scrollTop + clientHeight + 200 >= scrollHeight) {
          setIsIframeScrolled(true);
          detachScrollListener();
          iframe.removeEventListener("load", handleLoad);
          return;
        }
      };

      const attachScrollListener = () => {
        if (iframe.contentWindow) {
          iframe.contentWindow.addEventListener("scroll", handleScroll);
        }
      };

      const detachScrollListener = () => {
        if (iframe.contentWindow) {
          iframe.contentWindow.removeEventListener("scroll", handleScroll);
        }
      };

      const handleLoad = () => {
        attachScrollListener();
      };

      iframe.addEventListener("load", handleLoad);
    }

    if (!!contractData) {
      SignService.getTaskList(contractData?.document_uuid, user.id)
        .then((response) => {
          setTaskList(btoa(response.data));
        })
        .catch((error) => { });
    }
  }, [contractData]);

  return (
    <>
      <Container className="contract-form-container">
        <PageHeader content={PageTitleConstants.ContractForm} />
        <Card className="mx-3 summary-card survey-screen application-form">
          {!!currentStep && (
            <ContractFormStatuses
              currentStep={currentStep}
            ></ContractFormStatuses>
          )}

          {description && (
            <div class="declaration-info mx-3">
              <div className="info-container">
                <div className="description-element">
                  <FontAwesomeIcon
                    icon={faStar}
                    className="description-icon mt-1"
                  />
                  <div class="description-paragraph" data-id="33:4107">
                    {currentStep === M1_ContractStatuses.ContractToSign.value && timeToConclusionComponent}
                    <div
                      dangerouslySetInnerHTML={{
                        __html: description,
                      }}
                    />{" "}
                  </div>
                </div>
              </div>
            </div>
          )}

          {!!contractData &&
            !(currentStep === M1_ContractStatuses.ContactInPreparation.value) ? (
            <>
              {!(
                currentStep === M1_ContractStatuses.ContactInPreparation.value
              ) && (
                  <>
                    <iframe
                      title="embedded-content"
                      style={{ width: "100%", height: "500px", border: "none" }}
                      ref={iframeRef}
                      srcDoc={contractData?.html}
                      class="app-form-iframe"
                    />
                    {currentStep === M1_ContractStatuses.ContractToSign.value &&
                      !!timeToConclusionLeft && (
                        <CardBody>
                          <div className="scale-label"></div>
                          {`Pole jest wymagane` ? (
                            <div className="description-element">
                              <img
                                src={AppHelpers.svgBase64(IconInfo)}
                                className="description-icon"
                                alt="Informacja"
                              />
                              <p class="description-paragraph" data-id="33:4107">
                                Pole jest wymagane
                              </p>
                            </div>
                          ) : null}
                          <CheckboxControl
                            id={"rightsCheckbox"}
                            readOnly={!isIframeScrolled}
                            label="Zapoznałem się z zapisami Umowy o dofinansowanie usług w ramach projektu „Małopolski Pociąg do Kariery – sezon 1” oraz oświadczam, że przyjmuję do wiadomości zapisy zawarte w Oświadczeniu Uczestnika projektu „Małopolski pociąg do kariery - sezon 1"
                            onChange={(e) => setIsSignAvailable((prev) => !prev)}
                          />
                        </CardBody>
                      )}
                  </>
                )}
            </>
          ) : (
            <>
              {!isLoaded &&
                !(
                  currentStep === M1_ContractStatuses.ContactInPreparation.value
                ) && <Loading />}
            </>
          )}

          <Card.Footer className="bg-white">
            <Button
              className="btn-back"
              variant="secondary mr-1"
              onClick={goBack}
            >
              Powrót
            </Button>
            {!!contractData &&
              currentStep === M1_ContractStatuses.ContractAgreement.value && (
                <>
                  <Button
                    className="btn-next"
                    onClick={() => downloadContract(2)}
                    variant="primary"
                  >
                    <FontAwesomeIcon icon={faFile} />
                    Pobierz XML
                  </Button>
                  <Button
                    className="btn-next"
                    onClick={() => downloadContract(1)}
                    variant="primary"
                  >
                    <FontAwesomeIcon icon={faFilePdf} />
                    Pobierz PDF
                  </Button>
                </>
              )}
            {currentStep === M1_ContractStatuses.ContractToSign.value && !!timeToConclusionLeft ? (
              !hideSignButton &&
              <Button className="btn-next" variant="primary" disabled={!isSignAvailable} onClick={openSignModal}>
                <FontAwesomeIcon icon={faEdit} /> Podpisz
              </Button>
            ) : null}
          </Card.Footer>
        </Card>
      </Container>

      {currentStep === M1_ContractStatuses.ContractToSign.value &&
        !!timeToConclusionLeft ? (
        <SignApplicationModal
          isApplicationModalOpen={isApplicationModalOpen}
          setIsApplicationModalOpen={setIsApplicationModalOpen}
          formUuid={contractData?.document_uuid}
          clientId={user.id}
          taskList={taskList}
          documentType={1}
          contractNumber={contractNumber}
        />
      ) : null}
    </>
  );
}

export default ContractForm;
